/**
 * PlanLimitBanner — Issue #57
 *
 * Eigene Hotspots oberhalb des Plan-Kontingents (user_hotspots.active = false)
 * sind serverseitig aus allen Listen und Zaehlern ausgeblendet. v1 hat das im
 * ApplicationController explizit gemeldet, v2 tat es bislang gar nicht — die
 * Hotspots verschwanden lautlos und die Liste sah nach Datenverlust aus.
 * Der Banner benennt die Zahlen (sichtbar / gesamt / Limit) und fuehrt zum
 * Upgrade. Ohne ausgeblendete Hotspots liefert das BE keinen Block und der
 * Banner rendert nichts.
 *
 * Issue #67 — das BE unterscheidet seit Epic 6 zwei getrennte Kontingente
 * (Mobile/IoT); der alte, kombinierte `hotspot_limit` sagt nicht mehr, WELCHES
 * der beiden den Nutzer tatsaechlich blockiert. Liefert die Response die
 * neuen `mobile`/`iot`-Buckets (HotspotQuotaEnforcer#summary), zeigt der
 * Banner beide Kontingente; sonst faellt er auf den alten Text zurueck
 * (aeltere BE-Deploys ohne den Block).
 */
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'

export type HotspotQuotaBucket = {
  used: number
  limit: number | null
  /**
   * Issue #44/#67: Hotspots dieses Buckets ganz ohne `user_hotspots`-Zeile
   * (durchs Kontingent blockiert, bevor sie als "ausgeblendet" gezählt
   * werden) — optional, weil ältere BE-Deploys das Feld noch nicht liefern.
   */
  blocked?: number
}

export type PlanLimit = {
  hidden_hotspots: number
  visible_hotspots: number
  hotspot_limit: number
  current_plan: string
  mobile?: HotspotQuotaBucket | null
  iot?: HotspotQuotaBucket | null
}

export function PlanLimitBanner({ planLimit }: { planLimit?: PlanLimit | null }) {
  const { t } = useTranslation('common')

  if (!planLimit || planLimit.hidden_hotspots <= 0) return null

  const total = planLimit.visible_hotspots + planLimit.hidden_hotspots
  const hasBuckets =
    planLimit.mobile?.limit != null && planLimit.iot?.limit != null
  const blockedCount = (planLimit.mobile?.blocked ?? 0) + (planLimit.iot?.blocked ?? 0)

  return (
    <div className="card card-pad" role="status" data-testid="plan-limit-banner" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <AlertTriangle size={16} aria-hidden="true" />
        <strong>
          {t('planLimit.title', { hidden: planLimit.hidden_hotspots, total })}
        </strong>
        <span className="muted" style={{ fontSize: 13 }}>
          {hasBuckets
            ? t('planLimit.detailBuckets', {
                plan: planLimit.current_plan,
                mobileLimit: planLimit.mobile!.limit,
                iotLimit: planLimit.iot!.limit,
              })
            : t('planLimit.detail', { plan: planLimit.current_plan, limit: planLimit.hotspot_limit })}
        </span>
        {blockedCount > 0 && (
          <span className="muted" style={{ fontSize: 13 }} data-testid="plan-limit-blocked">
            {t('planLimit.blocked', { count: blockedCount })}
          </span>
        )}
        <Link to="/plans" className="btn btn-sm btn-primary" data-testid="plan-limit-upgrade">
          {t('planLimit.upgrade')}
        </Link>
      </div>
    </div>
  )
}
